import { JobItemCompany, JobItemIndividual } from './Jobs.queries';
import { format, parseISO } from 'date-fns';
import StatusDot from '../../components/Status/StatusDot';
import React from 'react';
import { Column } from '../../hooks/useTablePages';
import { Flex } from 'rebass';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';
import { flagOptions } from '../../components/BackflowTest/BackflowTestFlagsPicker';
import Icon from '../../components/Icon/Icon';

/*
id
status
jobId
job {
  address {
    id
    street1
    street2
    city
    state
    zip
    zipExt
  }
}
backflowTest {
  dateTested
  initialResult
  finalResult
  testingCompanySubmitted
  testerName
  backflowAssemblyEdited
  properlyInstalled
  testable

  backflowAssembly {
    model {
      name
      backflowAssemblyMake {
        name
      }
    }
    serialNumber
    size
    type
    location
  }
}
*/

export const companyColumns: Column[] = [
  {
    header: 'Job',
    id: 'jobId',
    accessor: (row: JobItemCompany) => {
      return (
        <Flex flexDirection="row" sx={{ gap: 1, alignItems: 'center' }}>
          {row.jobId}
          {row?.job?.attachments?.length && (
            <Icon title={'Has photos'} icon="image" size="16" />
          )}
        </Flex>
      );
    },
    stringAccessor: (row) => row.jobId,
    width: 100,
  },
  {
    header: 'Status',
    id: 'status',
    accessor: (row: JobItemCompany) => {
      return constantToLabel(
        row.status === 'COMPLETED' ? 'IN_REVIEW' : row.status
      );
    },
    width: 100,
  },
  {
    header: 'Billing',
    id: 'billingStatus',
    accessor: (row: JobItemCompany) => {
      return constantToLabel(row.billingStatus ?? undefined);
    },
    width: 80,
  },
  {
    header: 'Ordered',
    id: 'created',
    accessor: (row: JobItemCompany) => {
      return format(row.created!, 'M/d/yyyy');
    },
    width: 100,
  },
  {
    header: 'Customer',
    accessor: 'customerProfile.name',
    width: 190,
  },
  {
    header: 'Customer Phone',
    id: 'customerProfile.phones.value',
    accessor: (row: JobItemCompany) => {
      return row.customerProfile?.phones[0]?.value ?? '';
    },
    width: 130,
  },
  {
    header: 'Water Supplier',
    accessor: 'job.backflowTest.waterSupplierProfile.name',
    width: 190,
  },
  {
    header: 'Tester',
    accessor: 'child.vendorProfile.name',
    width: 140,
  },
  {
    // header: 'Due (Tester)',
    header: 'Due',
    id: 'child.due',
    accessor: (row: JobItemCompany) => {
      return row.child?.due ? format(row.child.due, 'M/d/yyyy') : '';
    },
    width: 100,
  },
  {
    header: 'Test Date',
    id: 'job.backflowTest.dateTested',
    accessor: (row: JobItemCompany) => {
      if (
        !row.job.backflowTest?.dateTested ||
        row.job.backflowTest.dateTested.startsWith('00')
      ) {
        return '';
      }
      return format(parseISO(row.job.backflowTest.dateTested), 'M/d/yyyy');
    },
    width: 100,
  },
  {
    header: 'Flags',
    id: 'flags',
    sortable: false,
    accessor: (row: JobItemCompany) => {
      if (!row.job.backflowTest) {
        return '';
      }
      const backflowTest = row.job.backflowTest;
      const matches = flagOptions.filter((option) => {
        for (const field in option.filter) {
          if (
            backflowTest?.[
              field as keyof JobItemCompany['job']['backflowTest']
            ] !== option.filter[field]
          ) {
            return false;
          }
        }
        return true;
      });

      const toDisplay = matches.filter(
        (option) =>
          option.value !== 'pass' ||
          !matches.find((opt) => opt.value === 'clear')
      );

      return (
        <Flex flexDirection="row" sx={{ gap: 1 }}>
          {toDisplay.map((option) => (
            <Flex title={option.label} flexDirection="row" key={option.value}>
              <StatusDot color={option.color} />
            </Flex>
          ))}
        </Flex>
      );
    },
    stringAccessor: (row: JobItemCompany): string => {
      if (!row.job.backflowTest) {
        return '';
      }
      const backflowTest = row.job.backflowTest;
      const matches = flagOptions.filter((option) => {
        for (const field in option.filter) {
          if (
            backflowTest?.[
              field as keyof JobItemCompany['job']['backflowTest']
            ] !== option.filter[field]
          ) {
            return false;
          }
        }
        return true;
      });

      const toDisplay = matches.filter(
        (option) =>
          option.value !== 'pass' ||
          !matches.find((opt) => opt.value === 'clear')
      );

      return toDisplay.map((option) => option.label).join(', ');
    },
    width: 70,
  },
  {
    header: 'Completed',
    id: 'completed',
    accessor: (row: JobItemIndividual) => {
      if (!row.completed) {
        return '';
      }
      return format(row.completed, 'M/d/yyyy');
    },
    width: 100,
  },
  {
    header: 'Submitted',
    id: 'submitted',
    accessor: (row: JobItemCompany) => {
      if (!row.submitted) {
        return '';
      }
      return format(row.submitted, 'M/d/yyyy');
    },
    width: 105,
  },
  {
    header: 'TRAC',
    accessor: 'job.backflowTest.backflowAssembly.waterService.pin',
    width: 80,
  },
  {
    header: 'Address',
    id: 'job.address.street1',
    accessor: (row: JobItemCompany) =>
      row.job.address.street1 +
      (row.job.address.street2 ? ', ' + row.job.address.street2 : ''),
    width: 250,
  },
  {
    header: 'City',
    accessor: 'job.address.city',
    width: 120,
  },
  {
    header: 'State',
    accessor: 'job.address.state',
    width: 80,
  },
  {
    header: 'Zip',
    id: 'job.address.zip',
    accessor: (row: JobItemCompany) =>
      row.job.address.zip +
      (row.job.address.zipExt ? '-' + row.job.address.zipExt : ''),
    width: 120,
  },
  {
    header: 'Make',
    accessor:
      'job.backflowTest.backflowAssembly.model.backflowAssemblyMake.name',
    width: 120,
  },
  {
    header: 'Model',
    accessor: 'job.backflowTest.backflowAssembly.model.name',
    width: 110,
  },
  {
    header: 'Serial',
    accessor: 'job.backflowTest.backflowAssembly.serialNumber',
    width: 110,
  },
  {
    header: 'Size',
    accessor: 'job.backflowTest.backflowAssembly.size',
    width: 80,
  },
  {
    header: 'Type',
    accessor: 'job.backflowTest.backflowAssembly.type',
    width: 80,
  },
  // {
  //   header: 'Job Type',
  //   id: 'job.type',
  //   accessor: (row: JobItemCompany) => constantToLabel(row.job.type),
  //   width: 120,
  // },
  {
    header: 'Location',
    accessor: 'job.backflowTest.backflowAssembly.location',
    width: 350,
  },
];

export const individualColumns: Column[] = [
  {
    header: 'Job',
    id: 'jobId',
    accessor: (row: JobItemCompany) => {
      return (
        <Flex flexDirection="row" sx={{ gap: 1, alignItems: 'center' }}>
          {row.jobId}
          {row?.job?.attachments?.length && (
            <Icon title={'Has photos'} icon="image" size="16" />
          )}
        </Flex>
      );
    },
    stringAccessor: (row) => row.jobId,
    width: 100,
  },
  {
    header: 'Status',
    id: 'status',
    accessor: (row: JobItemIndividual) => {
      return constantToLabel(row.status);
    },
    width: 100,
  },
  {
    header: 'Ordered',
    id: 'created',
    accessor: (row: JobItemIndividual) => {
      return format(row.created!, 'M/d/yyyy');
    },
    width: 100,
  },
  {
    header: 'Due',
    id: 'due',
    accessor: (row: JobItemIndividual) => {
      return row.due ? format(row.due, 'M/d/yyyy') : '';
    },
    width: 100,
  },
  {
    header: 'Customer',
    accessor: 'parent.customerProfile.name',
    width: 190,
  },
  {
    header: 'Customer Phone',
    id: 'parent.customerProfile.phones.value',
    accessor: (row: JobItemIndividual) => {
      return row.parent?.customerProfile?.phones[0]?.value ?? '';
    },
    width: 130,
  },
  {
    header: 'Water Supplier',
    accessor: 'job.backflowTest.backflowAssembly.waterSupplierProfile.name',
    width: 190,
  },
  {
    header: 'Test Date',
    id: 'job.backflowTest.dateTested',
    accessor: (row: JobItemIndividual) => {
      if (
        !row.job.backflowTest?.dateTested ||
        row.job.backflowTest.dateTested.startsWith('00')
      ) {
        return '';
      }
      return format(parseISO(row.job.backflowTest.dateTested), 'M/d/yyyy');
    },
    width: 100,
  },
  {
    header: 'Flags',
    id: 'flags',
    sortable: false,
    accessor: (row: JobItemIndividual) => {
      if (!row.job.backflowTest) {
        return '';
      }
      const backflowTest = row.job.backflowTest;
      const matches = flagOptions.filter((option) => {
        for (const field in option.filter) {
          if (
            backflowTest?.[
              field as keyof JobItemIndividual['job']['backflowTest']
            ] !== option.filter[field]
          ) {
            return false;
          }
        }
        return true;
      });

      const toDisplay = matches.filter(
        (option) =>
          option.value !== 'pass' ||
          !matches.find((opt) => opt.value === 'clear')
      );

      return (
        <Flex flexDirection="row" sx={{ gap: 1 }}>
          {toDisplay.map((option) => (
            <Flex title={option.label} flexDirection="row" key={option.value}>
              <StatusDot color={option.color} />
            </Flex>
          ))}
        </Flex>
      );
    },
    stringAccessor: (row: JobItemIndividual): string => {
      if (!row.job.backflowTest) {
        return '';
      }
      const backflowTest = row.job.backflowTest;
      const matches = flagOptions.filter((option) => {
        for (const field in option.filter) {
          if (
            backflowTest?.[
              field as keyof JobItemIndividual['job']['backflowTest']
            ] !== option.filter[field]
          ) {
            return false;
          }
        }
        return true;
      });

      const toDisplay = matches.filter(
        (option) =>
          option.value !== 'pass' ||
          !matches.find((opt) => opt.value === 'clear')
      );

      return toDisplay.map((option) => option.label).join(', ');
    },
    width: 70,
  },
  {
    header: 'Completed',
    id: 'completed',
    accessor: (row: JobItemIndividual) => {
      if (!row.completed) {
        return '';
      }
      return format(row.completed, 'M/d/yyyy');
    },
    width: 100,
  },
  {
    header: 'Submitted',
    id: 'submitted',
    accessor: (row: JobItemIndividual) => {
      if (!row.submitted) {
        return '';
      }
      return format(row.submitted, 'M/d/yyyy');
    },
    width: 105,
  },
  {
    header: 'Address',
    id: 'job.address.street1',
    accessor: (row: JobItemIndividual) =>
      row.job.address.street1 +
      (row.job.address.street2 ? ', ' + row.job.address.street2 : ''),
    width: 250,
  },
  {
    header: 'City',
    accessor: 'job.address.city',
    width: 120,
  },
  {
    header: 'State',
    accessor: 'job.address.state',
    width: 80,
  },
  {
    header: 'Zip',
    id: 'job.address.zip',
    accessor: (row: JobItemIndividual) =>
      row.job.address.zip +
      (row.job.address.zipExt ? '-' + row.job.address.zipExt : ''),
    width: 120,
  },
  {
    header: 'Make',
    accessor:
      'job.backflowTest.backflowAssembly.model.backflowAssemblyMake.name',
    width: 120,
  },
  {
    header: 'Model',
    accessor: 'job.backflowTest.backflowAssembly.model.name',
    width: 110,
  },
  {
    header: 'Serial',
    accessor: 'job.backflowTest.backflowAssembly.serialNumber',
    width: 110,
  },
  {
    header: 'Size',
    accessor: 'job.backflowTest.backflowAssembly.size',
    width: 80,
  },
  {
    header: 'Type',
    accessor: 'job.backflowTest.backflowAssembly.type',
    width: 80,
  },
  {
    header: 'Location',
    accessor: 'job.backflowTest.backflowAssembly.location',
    width: 350,
  },
];
